import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import Feature from 'ol/Feature';
import OlMap from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import { unByKey } from 'ol/Observable';
import Overlay from 'ol/Overlay';
import View from 'ol/View';
import type { EventsKey } from 'ol/events';
import { LineString, Point } from 'ol/geom';
import type Geometry from 'ol/geom/Geometry';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { ImageWMS, OSM, Vector as VectorSource } from 'ol/source';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import { Subscription } from 'rxjs';

import {
  BREDA_CENTER_LON_LAT,
  RD_NEW_PROJECTION,
  calculateDestinationCoordinate,
  isValidBearing,
  isValidCoordinate,
  lonLatToRdCoordinate,
  registerDutchRdProjection,
} from '../../map/observer-map.helpers';
import { BaseLayerId } from '../../models/base-layer.model';
import {
  getObserverPhotoKey,
  normalizeObserverPhotoId,
  ObserverPhotoViewModel,
} from '../../models/observer-photo.model';
import { ObserverPositionDto } from '../../models/observer-position-dto';
import { DetectedDroneMapLayerService } from '../../services/detected-drone-map-layer.service';
import { ObserverMapFacadeService } from '../../services/observer-map-facade.service';
import { ObserverMapViewportService } from '../../services/observer-map-viewport.service';
import { ObserverPhotoStoreService } from '../../services/observer-photo-store.service';

interface ObserverPhotoOverlayState {
  deviceId: string;
  observerId: number;
  currentPhotoId: string | null;
  overlay: Overlay | null;
  element: HTMLButtonElement | null;
  imageElement: HTMLImageElement | null;
  photoSubscription: Subscription | null;
  coordinate: [number, number];
}

@Component({
  selector: 'app-observer-map-canvas',
  templateUrl: './observer-map-canvas.component.html',
  styleUrl: './observer-map-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObserverMapCanvasComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapElement', { static: true })
  private mapElement!: ElementRef<HTMLDivElement>;

  private readonly facade = inject(ObserverMapFacadeService);
  private readonly detectedDroneLayer = inject(DetectedDroneMapLayerService);
  private readonly photoStore = inject(ObserverPhotoStoreService);
  private readonly viewport = inject(ObserverMapViewportService);
  private readonly ngZone = inject(NgZone);

  private readonly observerSource = new VectorSource<Feature<Geometry>>();
  private readonly observerPhotoStates = new Map<string, ObserverPhotoOverlayState>();
  private observerPointStyle: Style | null = null;
  private observerBearingLineStyle: Style | null = null;
  private map: OlMap | null = null;
  private osmBaseLayer: TileLayer<OSM> | null = null;
  private orthoBaseLayer: ImageLayer<ImageWMS> | null = null;
  private mapSingleClickKey: EventsKey | null = null;
  private readonly observerRenderEffect = effect(() => {
    this.renderObservers(this.facade.observers());
  });
  private readonly detectedDroneMapEffect = effect(() => {
    this.detectedDroneLayer.updateDrones(this.facade.detectedDrones());
  });
  private readonly droneLayerVisibilityEffect = effect(() => {
    this.detectedDroneLayer.setVisible(this.facade.isDroneLayerVisible());
  });
  private readonly selectedDroneMapEffect = effect(() => {
    this.detectedDroneLayer.setSelectedDroneId(this.facade.selectedDroneId());
  });
  private readonly baseLayerVisibilityEffect = effect(() => {
    this.updateVisibleBaseLayer(this.facade.baseLayer());
  });
  private readonly entityListLayoutEffect = effect(() => {
    this.facade.isEntityListOpen();
    this.viewport.updateSize();
  });

  ngAfterViewInit(): void {
    this.createMap();
    this.renderObservers(this.facade.observers());
  }

  ngOnDestroy(): void {
    if (this.mapSingleClickKey) {
      unByKey(this.mapSingleClickKey);
      this.mapSingleClickKey = null;
    }

    this.detectedDroneLayer.detachFromMap();
    this.clearObserverPhotoOverlays();

    if (this.map) {
      this.viewport.clearMap(this.map);
      this.map.setTarget(undefined);
      this.map = null;
    }
  }

  private createMap(): void {
    registerDutchRdProjection();

    this.osmBaseLayer = new TileLayer({
      source: new OSM(),
    });
    this.orthoBaseLayer = new ImageLayer({
      visible: false,
      source: new ImageWMS({
        url: 'https://service.pdok.nl/hwh/luchtfotorgb/wms/v1_0',
        params: {
          LAYERS: 'Actueel_orthoHR',
          FORMAT: 'image/jpeg',
          VERSION: '1.3.0',
        },
        attributions: 'PDOK Luchtfoto RGB',
        crossOrigin: 'anonymous',
        ratio: 1,
      }),
    });

    this.map = new OlMap({
      target: this.mapElement.nativeElement,
      layers: [
        this.osmBaseLayer,
        this.orthoBaseLayer,
        new VectorLayer({
          source: this.observerSource,
        }),
      ],
      view: new View({
        center: lonLatToRdCoordinate(BREDA_CENTER_LON_LAT),
        projection: RD_NEW_PROJECTION,
        zoom: 14,
      }),
    });

    this.viewport.setMap(this.map);
    this.updateVisibleBaseLayer(this.facade.baseLayer());
    this.detectedDroneLayer.attachToMap(this.map);
    this.mapSingleClickKey = this.map.on('singleclick', this.handleMapSingleClick);
  }

  private renderObservers(observers: ObserverPositionDto[]): void {
    if (!this.map) {
      return;
    }

    const features = this.createObserverFeatures(observers);

    this.observerSource.clear();
    this.observerSource.addFeatures(features);
    this.updateObserverPhotoOverlays(observers);
  }

  private updateVisibleBaseLayer(baseLayer: BaseLayerId): void {
    this.osmBaseLayer?.setVisible(baseLayer === 'osm');
    this.orthoBaseLayer?.setVisible(baseLayer === 'ortho');
  }

  private updateObserverPhotoOverlays(observers: ObserverPositionDto[]): void {
    const visibleObserverKeys = new Set<string>();

    for (const observer of observers) {
      if (!isValidCoordinate(observer.longitude, observer.latitude)) {
        continue;
      }

      const observerKey = getObserverPhotoKey(observer);
      visibleObserverKeys.add(observerKey);
      this.syncObserverPhotoOverlay(observerKey, observer);
    }

    for (const observerKey of Array.from(this.observerPhotoStates.keys())) {
      if (!visibleObserverKeys.has(observerKey)) {
        this.removeObserverPhotoState(observerKey);
      }
    }
  }

  private syncObserverPhotoOverlay(observerKey: string, observer: ObserverPositionDto): void {
    const coordinate = lonLatToRdCoordinate([observer.longitude, observer.latitude]);
    const state = this.getOrCreateObserverPhotoState(observerKey, observer);
    const photoId = normalizeObserverPhotoId(observer.photoId);

    state.deviceId = observer.deviceId;
    state.observerId = observer.observerId;
    state.coordinate = coordinate;
    state.overlay?.setPosition(coordinate);

    if (state.currentPhotoId === photoId) {
      return;
    }

    state.photoSubscription?.unsubscribe();
    state.photoSubscription = null;
    state.currentPhotoId = photoId;

    if (!photoId) {
      this.removeObserverPhotoBubble(state);
      return;
    }

    this.removeObserverPhotoBubble(state);

    state.photoSubscription = this.photoStore.loadPhoto(photoId).subscribe({
      next: (photo) => {
        if (state.currentPhotoId !== photo.photoId || !this.observerPhotoStates.has(observerKey)) {
          return;
        }

        this.showObserverPhotoBubble(state, photo);
      },
      error: (error: unknown) => {
        if (state.currentPhotoId === photoId) {
          this.removeObserverPhotoBubble(state);
        }

        console.error('Failed to load observer photo.', error);
      },
    });
  }

  private getOrCreateObserverPhotoState(
    observerKey: string,
    observer: ObserverPositionDto,
  ): ObserverPhotoOverlayState {
    const existingState = this.observerPhotoStates.get(observerKey);

    if (existingState) {
      return existingState;
    }

    const state: ObserverPhotoOverlayState = {
      deviceId: observer.deviceId,
      observerId: observer.observerId,
      currentPhotoId: null,
      overlay: null,
      element: null,
      imageElement: null,
      photoSubscription: null,
      coordinate: lonLatToRdCoordinate([observer.longitude, observer.latitude]),
    };

    this.observerPhotoStates.set(observerKey, state);

    return state;
  }

  private showObserverPhotoBubble(
    state: ObserverPhotoOverlayState,
    photo: ObserverPhotoViewModel,
  ): void {
    this.removeObserverPhotoBubble(state);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'observer-photo-bubble';
    button.setAttribute('aria-label', `Open photo for observer ${state.observerId}`);

    const image = document.createElement('img');
    image.src = photo.imageUrl;
    image.alt = '';
    image.decoding = 'async';
    image.loading = 'lazy';

    button.appendChild(image);
    button.addEventListener('click', () => {
      this.ngZone.run(() => this.facade.openObserverPhoto(photo));
    });

    const overlay = new Overlay({
      element: button,
      offset: [0, -36],
      positioning: 'bottom-center',
      stopEvent: true,
    });

    this.map?.addOverlay(overlay);
    overlay.setPosition(state.coordinate);

    state.element = button;
    state.imageElement = image;
    state.overlay = overlay;
  }

  private removeObserverPhotoBubble(state: ObserverPhotoOverlayState): void {
    if (state.overlay) {
      this.map?.removeOverlay(state.overlay);
      state.overlay = null;
    }

    state.element?.remove();
    state.element = null;
    state.imageElement = null;
  }

  private removeObserverPhotoState(observerKey: string): void {
    const state = this.observerPhotoStates.get(observerKey);

    if (!state) {
      return;
    }

    state.photoSubscription?.unsubscribe();
    state.photoSubscription = null;
    this.removeObserverPhotoBubble(state);
    this.observerPhotoStates.delete(observerKey);
  }

  private clearObserverPhotoOverlays(): void {
    for (const observerKey of Array.from(this.observerPhotoStates.keys())) {
      this.removeObserverPhotoState(observerKey);
    }
  }

  private readonly handleMapSingleClick = (event: MapBrowserEvent): void => {
    if (!this.map) {
      return;
    }

    this.facade.selectDroneId(this.detectedDroneLayer.getDroneIdAtPixel(this.map, event.pixel));
  };

  private createObserverFeatures(observers: ObserverPositionDto[]): Feature<Geometry>[] {
    return observers
      .filter((observer) => isValidCoordinate(observer.longitude, observer.latitude))
      .flatMap((observer) => {
        const pointFeature = this.createPointFeature(observer);
        const bearingLineFeature = this.createBearingLineFeature(observer);

        return bearingLineFeature ? [bearingLineFeature, pointFeature] : [pointFeature];
      });
  }

  private createPointFeature(observer: ObserverPositionDto): Feature<Point> {
    const feature = new Feature({
      geometry: new Point(lonLatToRdCoordinate([observer.longitude, observer.latitude])),
      observerId: observer.observerId,
      deviceId: observer.deviceId,
      receivedAtUtc: observer.receivedAtUtc,
    });

    const labelStyle = this.getObserverPointStyle().clone();
    labelStyle.getText()?.setText(String(observer.observerId));
    feature.setStyle(labelStyle);

    return feature;
  }

  private createBearingLineFeature(observer: ObserverPositionDto): Feature<LineString> | null {
    if (!isValidBearing(observer.bearingDegrees)) {
      return null;
    }

    const startLonLat: [number, number] = [observer.longitude, observer.latitude];
    const endLonLat = calculateDestinationCoordinate(
      observer.longitude,
      observer.latitude,
      observer.bearingDegrees,
      1000,
    );
    const feature = new Feature({
      geometry: new LineString([
        lonLatToRdCoordinate(startLonLat),
        lonLatToRdCoordinate(endLonLat),
      ]),
      observerId: observer.observerId,
      deviceId: observer.deviceId,
      receivedAtUtc: observer.receivedAtUtc,
      bearingDegrees: observer.bearingDegrees,
    });

    feature.setStyle(this.getObserverBearingLineStyle());

    return feature;
  }

  private getObserverPointStyle(): Style {
    this.observerPointStyle ??= new Style({
      image: new CircleStyle({
        radius: this.readStyleNumber('--observer-point-radius'),
        fill: new Fill({ color: this.readStyleValue('--observer-point-fill') }),
        stroke: new Stroke({
          color: this.readStyleValue('--observer-point-stroke'),
          width: this.readStyleNumber('--observer-point-stroke-width'),
        }),
      }),
      text: new Text({
        offsetY: this.readStyleNumber('--observer-label-offset-y'),
        fill: new Fill({ color: this.readStyleValue('--observer-label-fill') }),
        stroke: new Stroke({
          color: this.readStyleValue('--observer-label-stroke'),
          width: this.readStyleNumber('--observer-label-stroke-width'),
        }),
        font: this.readStyleValue('--observer-label-font'),
      }),
    });

    return this.observerPointStyle;
  }

  private getObserverBearingLineStyle(): Style {
    this.observerBearingLineStyle ??= new Style({
      stroke: new Stroke({
        color: this.readStyleValue('--observer-bearing-stroke'),
        width: this.readStyleNumber('--observer-bearing-stroke-width'),
        lineCap: this.readStyleValue('--observer-bearing-line-cap') as CanvasLineCap,
      }),
    });

    return this.observerBearingLineStyle;
  }

  private readStyleValue(propertyName: string): string {
    return getComputedStyle(this.mapElement.nativeElement).getPropertyValue(propertyName).trim();
  }

  private readStyleNumber(propertyName: string): number {
    return Number(this.readStyleValue(propertyName));
  }
}
